import React from 'react';
import { CheckCircle, MapPin, Building, Calendar } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const RumailaOilFieldPage = () => {
  const deliverables = [
    'Pipe wall thickness Calculations',
    'Piping & Instrumentation Diagrams (P & IDs)',
    'Pipeline Alignment Sheets',
    'Piping Isometrics (including tie-in) and MTO estimation',
    'General Arrangement Drawings (GADs) for support location and support detailing',
    'Road crossing details',
    'Datasheet, Material requisition (MRs) and Technical Bid Evaluation (TBE) for Valve and Pipe',
    'Stress Analysis'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage: 'url(https://images.pexels.com/photos/15961091/pexels-photo-15961091.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)'
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-900/70" />
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Rumaila Oil Field Development
          </h1>
          <div className="flex flex-wrap justify-center gap-6 mt-6">
            <div className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              <span className="text-lg">Basra, Iraq</span>
            </div>
            <div className="flex items-center gap-2">
              <Building className="w-5 h-5" />
              <span className="text-lg">BP</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span className="text-lg">2019-2023</span>
            </div>
          </div>
        </div>
      </section>

      {/* Project Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Project <span className="text-red-600">Overview</span>
            </h2>
            <div className="w-24 h-1 bg-red-600 mb-8" />
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              World's third biggest producing field at 1.5 million barrels per day, delivering approximately one-third of 
              Iraq's total oil supply. Operating since 1954 with 17 billion barrels of recoverable reserves.
            </p>
          </div>

          {/* Key Stats */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">1.5M</div>
                <div className="text-red-100">Barrels Per Day</div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">17B</div>
                <div className="text-red-100">Recoverable Reserves (Barrels)</div>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-indigo-800 text-white">
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold mb-2">1954</div>
                <div className="text-red-100">Operational Since</div>
              </CardContent>
            </Card>
          </div>

          {/* Detailed Engineering Services */}
          <div>
            <h3 className="text-2xl font-bold text-slate-900 mb-6">
              Detailed Engineering Services for GRE Pipeline - Key Deliverables
            </h3>
            <p className="text-slate-700 mb-6">
              Total Vision provided comprehensive engineering services for GRE Pipeline development:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {deliverables.map((deliverable, index) => (
                <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                      <span className="text-slate-700 text-base font-medium">{deliverable}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Project Significance */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-3xl font-bold text-slate-900 mb-6">Project Significance</h3>
            <p className="text-lg text-slate-700 leading-relaxed">
              The Rumaila Oil Field is one of the world's most significant petroleum assets, and Total Vision's contribution 
              through comprehensive GRE Pipeline engineering services has been instrumental in maintaining and enhancing its 
              operational capabilities. Our detailed engineering deliverables ensure the continued efficient and safe 
              transportation of hydrocarbons from this critical field that supplies approximately one-third of Iraq's oil production.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RumailaOilFieldPage;